import { useState, useEffect, useRef } from 'react';
import { Sparkles, Send, X, Trash2, MessageCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const SESSION_KEY = 'medcare_ai_tutor_session';

/**
 * Floating AI Tutor chat bubble.
 *
 * Props:
 *  - context: { course_id?, course_title?, module_number?, module_title? }
 *  - userId?: optional logged-in user id (for analytics)
 */
const AITutorChat = ({ context = {}, userId = null }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, timestamp}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem(SESSION_KEY) || '';
    } catch (_e) {
      return '';
    }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (sessionId) {
      try { localStorage.setItem(SESSION_KEY, sessionId); } catch (_e) {}
    }
  }, [sessionId]);

  // Load existing history when opened for the first time and we have a session.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!open || !sessionId || messages.length > 0) return;
      try {
        const r = await fetch(`${API}/api/ai-tutor/history/${sessionId}`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && Array.isArray(data.history)) {
          setMessages(data.history);
        }
      } catch (_e) { /* ignore */ }
    };
    load();
    return () => { cancelled = true; };
  }, [open, sessionId, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    const optimistic = [
      ...messages,
      { role: 'user', content: text, timestamp: new Date().toISOString() },
    ];
    setMessages(optimistic);
    try {
      const r = await fetch(`${API}/api/ai-tutor/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId || null,
          user_id: userId,
          course_id: context.course_id,
          course_title: context.course_title,
          module_number: context.module_number,
          module_title: context.module_title,
          message: text,
        }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        setMessages([
          ...optimistic,
          { role: 'assistant', content: `Sorry — I couldn't reach the tutor service. (${errBody.detail || r.status})`, timestamp: new Date().toISOString() },
        ]);
        return;
      }
      const data = await r.json();
      if (data.session_id) setSessionId(data.session_id);
      if (Array.isArray(data.history) && data.history.length > 0) {
        setMessages(data.history);
      } else if (data.reply) {
        setMessages([
          ...optimistic,
          { role: 'assistant', content: data.reply, timestamp: new Date().toISOString() },
        ]);
      }
    } catch (e) {
      setMessages([
        ...optimistic,
        { role: 'assistant', content: 'Network error — please try again.', timestamp: new Date().toISOString() },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (sessionId) {
      try { await fetch(`${API}/api/ai-tutor/history/${sessionId}`, { method: 'DELETE' }); } catch (_e) {}
    }
    setMessages([]);
    setSessionId('');
    try { localStorage.removeItem(SESSION_KEY); } catch (_e) {}
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          data-testid="ai-tutor-launcher"
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            background: 'linear-gradient(135deg,#7c3aed 0%,#2563eb 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            zIndex: 50,
            fontWeight: 600,
            fontSize: '0.92rem',
          }}
        >
          <Sparkles size={18} />
          <span>Ask MedCare Tutor</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-tutor-panel"
          style={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            width: 'min(420px, calc(100vw - 32px))',
            height: 'min(620px, calc(100vh - 60px))',
            background: '#ffffff',
            borderRadius: 16,
            boxShadow: '0 18px 50px rgba(15,23,42,0.28)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 60,
            border: '1px solid #e2e8f0',
          }}
        >
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg,#7c3aed 0%,#2563eb 100%)',
            color: '#fff',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 999,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles size={20} />
            </div>
            <div style={{ flex: 1, lineHeight: 1.2 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>MedCare Tutor</div>
              <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>
                {context.course_title
                  ? `Course: ${context.course_title}${context.module_number ? ` · Module ${context.module_number}` : ''}`
                  : 'Apprenticeship Q&A · Powered by Claude'}
              </div>
            </div>
            <button
              type="button"
              data-testid="ai-tutor-reset"
              onClick={reset}
              title="Reset conversation"
              style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 6 }}
            >
              <Trash2 size={16} />
            </button>
            <button
              type="button"
              data-testid="ai-tutor-close"
              onClick={() => setOpen(false)}
              title="Close"
              style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 6 }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="ai-tutor-messages"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 14,
              background: '#f8fafc',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {messages.length === 0 && (
              <div style={{
                textAlign: 'center',
                color: '#64748b',
                fontSize: '0.86rem',
                marginTop: 18,
              }}>
                <MessageCircle size={28} style={{ color: '#7c3aed', marginBottom: 8 }} />
                <p style={{ margin: '6px 0 4px', fontWeight: 600, color: '#0b2545' }}>Ask anything about your course</p>
                <p style={{ margin: 0 }}>I can help you understand modules, prepare for the mid-term &amp; final, and explain medical terms.</p>
              </div>
            )}
            {messages.map((m, idx) => (
              <div
                key={idx}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '88%',
                  background: m.role === 'user' ? '#2563eb' : '#ffffff',
                  color: m.role === 'user' ? '#ffffff' : '#0f172a',
                  border: m.role === 'user' ? 'none' : '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: '10px 12px',
                  fontSize: '0.88rem',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                }}
                data-testid={`ai-tutor-msg-${m.role}`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div style={{
                alignSelf: 'flex-start',
                color: '#64748b',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <Loader2 size={14} className="spin" /> MedCare Tutor is thinking…
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{
            borderTop: '1px solid #e2e8f0',
            padding: 10,
            display: 'flex',
            gap: 8,
            background: '#ffffff',
          }}>
            <textarea
              data-testid="ai-tutor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={context.course_title ? `Ask about ${context.course_title}…` : 'Ask the tutor…'}
              rows={2}
              style={{
                flex: 1,
                resize: 'none',
                border: '1px solid #cbd5e1',
                borderRadius: 10,
                padding: '8px 10px',
                fontSize: '0.88rem',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              data-testid="ai-tutor-send"
              onClick={send}
              disabled={!input.trim() || sending}
              style={{
                background: input.trim() && !sending ? 'linear-gradient(135deg,#7c3aed,#2563eb)' : '#cbd5e1',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '0 14px',
                cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes ai-spin { to { transform: rotate(360deg); } } .spin { animation: ai-spin 1s linear infinite; }`}</style>
    </>
  );
};

export default AITutorChat;
